import clsx from 'clsx'
import { FolderFilled, SyncAlert, SyncDone } from '@/assets/icons'
import { kExternalStorageProvider, type ExternalStorageProvider } from '@/constants/constants-external-storage'
import { PROVIDER_COMPACT_DISPLAY_NAME, PROVIDER_ICON_COMPONENTS } from '@/user_files/external-storage/ProviderMeta'
import { ContextRailDriveSyncStatus } from '@/matter/components/matter-overview/context-rail-drive-sync-status'

/** Provider avatars stacked by the NotConnected bar, in design order. */
const DISCOVERABILITY_PROVIDERS: ExternalStorageProvider[] = [
  kExternalStorageProvider.BOX,
  kExternalStorageProvider.DROPBOX,
  kExternalStorageProvider.ONEDRIVE,
  kExternalStorageProvider.GOOGLE_DRIVE,
]

// Provider logos have different intrinsic aspect ratios, so one `size-*` class distorts them.
// Width is pinned to the slot and height derived from each logo's own viewBox — the same
// per-provider approach DisconnectedStorageItem already uses.
const PROVIDER_GLYPH_16: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: 'w-4 h-[14px]',
  [kExternalStorageProvider.DROPBOX]: 'w-4 h-[15px]',
  [kExternalStorageProvider.BOX]: 'w-4 h-[9px]',
  [kExternalStorageProvider.ONEDRIVE]: 'w-4 h-[11px]',
  [kExternalStorageProvider.CLIO]: 'size-4',
  [kExternalStorageProvider.SHAREPOINT]: 'size-4',
}

const PROVIDER_GLYPH_12: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: 'w-3 h-[11px]',
  [kExternalStorageProvider.DROPBOX]: 'w-3 h-[11px]',
  [kExternalStorageProvider.BOX]: 'w-3 h-[7px]',
  [kExternalStorageProvider.ONEDRIVE]: 'w-3 h-2',
  [kExternalStorageProvider.CLIO]: 'size-3',
  [kExternalStorageProvider.SHAREPOINT]: 'size-3',
}

// Each status sets its text colour against its own background rather than sharing one default,
// and hover or keyboard focus resolves to Brand Neutral 200 for every status except NotConnected.
const STATUS_STYLES: Record<ContextRailDriveSyncStatus, { container: string; text: string }> = {
  [ContextRailDriveSyncStatus.Synced]: {
    container: 'bg-brand-neutral-100 border-brand-neutral-200 hocus:bg-brand-neutral-200',
    text: 'text-brand-neutral-800',
  },
  [ContextRailDriveSyncStatus.Disconnected]: {
    container: 'bg-red-50 border-red-200 hocus:border-brand-neutral-200 hocus:bg-brand-neutral-200',
    text: 'text-brand-neutral-950',
  },
  [ContextRailDriveSyncStatus.NotConnected]: {
    container: 'bg-brand-50 border-brand-100 hocus:bg-brand-100',
    text: 'text-black',
  },
  [ContextRailDriveSyncStatus.NoFolderSynced]: {
    container: 'bg-yellow-50 border-brand-neutral-200 hocus:bg-brand-neutral-200',
    text: 'text-black',
  },
}

type ContextRailDriveSyncBarProps = {
  status: ContextRailDriveSyncStatus
  /** Named in the copy and shown as a glyph. Null only in NotConnected, which has no account to name. */
  provider: ExternalStorageProvider | null
  /** Full sync path shown on hover or keyboard focus, e.g. "MyDrive/Matters/Vista Drywall". Null suppresses the label. */
  syncedFolderPath?: string | null
  /** Whether this user has permission to reconnect. */
  canReconnect?: boolean
  /** Reconnect, connect a drive, pick a folder, or open the synced connection — whichever the status calls for. */
  onAction?: () => void
}

function ProviderFolderIcon({ provider }: { provider: ExternalStorageProvider }) {
  const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]

  return (
    <span className="relative size-5 shrink-0" data-provider={provider} aria-hidden="true">
      <FolderFilled className="absolute inset-0 size-5 text-true-gray-300" />
      <span className="absolute left-[3px] top-[7px] flex h-[9px] w-3.5 items-center justify-center">
        <ProviderIcon className={PROVIDER_GLYPH_12[provider]} />
      </span>
    </span>
  )
}

function ProviderAvatarStack() {
  return (
    <span className="flex shrink-0 items-center" aria-hidden="true">
      {DISCOVERABILITY_PROVIDERS.map((provider, index) => {
        const ProviderIcon = PROVIDER_ICON_COMPONENTS[provider]

        return (
          <span
            key={provider}
            data-provider={provider}
            className={clsx(
              'flex size-5 shrink-0 items-center justify-center rounded-full border border-brand-neutral-200 bg-white',
              index < DISCOVERABILITY_PROVIDERS.length - 1 && '-mr-1.5'
            )}
          >
            <ProviderIcon className={PROVIDER_GLYPH_12[provider]} />
          </span>
        )
      })}
    </span>
  )
}

export function ContextRailDriveSyncBar({ status, provider, syncedFolderPath, canReconnect = false, onAction }: ContextRailDriveSyncBarProps) {
  const styles = STATUS_STYLES[status]
  const providerLabel = provider ? PROVIDER_COMPACT_DISPLAY_NAME[provider] : null
  const statusLabel = getLabel(status, providerLabel, canReconnect)
  const ProviderIcon = provider ? PROVIDER_ICON_COMPONENTS[provider] : null

  const className = clsx(
    'group relative flex w-full items-center gap-1.5 rounded-b-lg border px-4 text-left transition-colors',
    // NoFolderSynced carries a 20px folder icon, so it needs 6px of padding rather than 4px to hold
    // the bar at its 32px minimum.
    status === ContextRailDriveSyncStatus.NoFolderSynced ? 'min-h-8 py-1.5' : 'min-h-8 py-1',
    styles.container,
    styles.text,
    'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500'
  )

  const showPathLabel = status === ContextRailDriveSyncStatus.Synced && Boolean(syncedFolderPath)

  const ariaLabel = showPathLabel ? `${statusLabel}, ${syncedFolderPath}` : undefined
  const pathLabel = showPathLabel ? (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -top-[18px] left-[-1px] max-w-full truncate rounded-xs bg-brand-neutral-800 px-1 py-0.5 text-2xs font-medium leading-[10.4px] tracking-[0.1248px] text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
    >
      {`Synced to ${syncedFolderPath}`}
    </span>
  ) : null

  // The bar is a control in every status, so it stays one consistent click and focus target rather
  // than the CTA statuses being actionable and Synced being inert text.
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      data-testid="context-rail-drive-sync-bar"
      data-drive-sync-status={status}
      className={className}
      onClick={onAction}
    >
      {status === ContextRailDriveSyncStatus.NotConnected ? (
        <>
          <span className="min-w-0 flex-1 text-xs font-medium leading-4">{statusLabel}</span>
          <ProviderAvatarStack />
        </>
      ) : (
        <>
          {status === ContextRailDriveSyncStatus.Disconnected && <SyncAlert className="size-4 shrink-0 text-red-700" aria-hidden="true" />}
          {ProviderIcon && status !== ContextRailDriveSyncStatus.NoFolderSynced && (
            <span className="flex size-4 shrink-0 items-center justify-center" data-provider={provider} aria-hidden="true">
              <ProviderIcon className={PROVIDER_GLYPH_16[provider as ExternalStorageProvider]} />
            </span>
          )}
          {status === ContextRailDriveSyncStatus.Synced && <SyncDone className="size-4 shrink-0 text-brand-neutral-800" aria-hidden="true" />}
          {status === ContextRailDriveSyncStatus.NoFolderSynced && provider && <ProviderFolderIcon provider={provider} />}
          <span className="min-w-0 truncate text-xs font-medium leading-4">{statusLabel}</span>
          {pathLabel}
        </>
      )}
    </button>
  )
}

function getLabel(status: ContextRailDriveSyncStatus, providerLabel: string | null, canReconnect: boolean) {
  switch (status) {
    case ContextRailDriveSyncStatus.Synced:
      return `Synced to ${providerLabel} folder`
    case ContextRailDriveSyncStatus.Disconnected:
      return canReconnect ? `Reconnect ${providerLabel}` : `${providerLabel} Disconnected`
    case ContextRailDriveSyncStatus.NoFolderSynced:
      return `Sync a ${providerLabel} folder to this matter`
    case ContextRailDriveSyncStatus.NotConnected:
      return 'Connect a file drive'
  }
}
